import { getDriverApprovalStatus } from '../../utils/driverDocuments';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../config/axiosConfig';
import { extractPayload, getErrorMessage } from '../../utils/apiResponseUtils';

const normalizeDocumentRecord = (document = {}) => ({
  ...document,
  name: document.name || document.fileName || document.file_name || document.originalName || document.original_name || '',
  type: document.type || document.contentType || document.content_type || '',
  dataUrl:
    document.dataUrl ||
    document.data_url ||
    document.fileUrl ||
    document.file_url ||
    document.downloadUrl ||
    document.download_url ||
    document.filePath ||
    document.file_path ||
    document.path ||
    document.url ||
    '',
  uploadedAt: document.uploadedAt || document.uploaded_at || document.createdAt || document.created_at || null,
});

const normalizeAdminDocuments = (documents) => {
  if (!documents) {
    return {};
  }

  if (Array.isArray(documents)) {
    return documents.reduce((normalizedDocuments, document) => {
      const documentType = String(document.documentType || document.document_type || document.type || '').toLowerCase();
      const key = documentType === 'aadhaar' ? 'aadhar' : documentType;

      if (['aadhar', 'dl', 'rc'].includes(key)) {
        normalizedDocuments[key] = normalizeDocumentRecord(document);
      }

      return normalizedDocuments;
    }, {});
  }

  return {
    aadhar: documents.aadhar || documents.aadhaar || documents.AADHAR || documents.AADHAAR
      ? normalizeDocumentRecord(documents.aadhar || documents.aadhaar || documents.AADHAR || documents.AADHAAR)
      : undefined,
    dl: documents.dl || documents.DL || documents.license || documents.drivingLicense
      ? normalizeDocumentRecord(documents.dl || documents.DL || documents.license || documents.drivingLicense)
      : undefined,
    rc: documents.rc || documents.RC || documents.vehicleRc || documents.vehicleRegistration
      ? normalizeDocumentRecord(documents.rc || documents.RC || documents.vehicleRc || documents.vehicleRegistration)
      : undefined,
  };
};

const normalizeAdminDriver = (driver) => {
  const payload = extractPayload(driver) || driver || {};
  const source =
    payload.driver ||
    payload.user ||
    payload.driverDetails ||
    payload.userDetails ||
    payload.profile ||
    payload;

  return {
    ...payload,
    ...source,
    id: source.id || payload.id || source.driverId || payload.driverId || source.userId || payload.userId || source._id || payload._id || '',
    firstName: source.firstName || source.first_name || payload.firstName || payload.first_name || '',
    lastName: source.lastName || source.last_name || payload.lastName || payload.last_name || '',
    email: source.email || payload.email || '',
    phone:
      source.phone ||
      source.mobile ||
      source.mobileNumber ||
      source.phoneNumber ||
      payload.phone ||
      payload.mobile ||
      payload.mobileNumber ||
      payload.phoneNumber ||
      '',
    vehicleNumber:
      source.vehicleNumber ||
      source.vehicle_number ||
      source.vehicleNo ||
      source.vehicle_no ||
      source.vehicleRegistrationNumber ||
      source.vehicle_registration_number ||
      source.registrationNumber ||
      source.registration_number ||
      payload.vehicleNumber ||
      payload.vehicle_number ||
      payload.vehicleNo ||
      payload.vehicle_no ||
      payload.vehicleRegistrationNumber ||
      payload.vehicle_registration_number ||
      payload.registrationNumber ||
      payload.registration_number ||
      '',
    licenseNumber:
      source.licenseNumber ||
      source.license_number ||
      source.licenseNo ||
      source.license_no ||
      source.dlNumber ||
      source.dl_number ||
      source.drivingLicenseNumber ||
      source.driving_license_number ||
      payload.licenseNumber ||
      payload.license_number ||
      payload.licenseNo ||
      payload.license_no ||
      payload.dlNumber ||
      payload.dl_number ||
      payload.drivingLicenseNumber ||
      payload.driving_license_number ||
      '',
    approvalStatus: getDriverApprovalStatus({ ...payload, ...source }),
    active: Boolean(source.active ?? payload.active ?? true),
    available: Boolean(source.available ?? payload.available ?? false),
    approvalReason:
      source.approvalReason ||
      source.approval_reason ||
      payload.approvalReason ||
      payload.approval_reason ||
      '',
    rejectionReason:
      source.rejectionReason ||
      source.rejection_reason ||
      source.approvalReason ||
      source.approval_reason ||
      payload.rejectionReason ||
      payload.rejection_reason ||
      payload.approvalReason ||
      payload.approval_reason ||
      '',
    documents: normalizeAdminDocuments(
      source.documents ||
      source.driverDocuments ||
      source.uploadedDocuments ||
      payload.documents ||
      payload.driverDocuments ||
      payload.uploadedDocuments ||
      {}
    ),
    createdAt: source.createdAt || source.created_at || payload.createdAt || payload.created_at || null,
    approvedAt: source.approvedAt || source.approved_at || payload.approvedAt || payload.approved_at || null,
    rejectedAt: source.rejectedAt || source.rejected_at || payload.rejectedAt || payload.rejected_at || null,
  };
};

const normalizeAdminCustomer = (customer) => {
  const payload = extractPayload(customer) || customer || {};
  const source =
    payload.customer ||
    payload.user ||
    payload.customerDetails ||
    payload.userDetails ||
    payload.profile ||
    payload;

  return {
    ...payload,
    ...source,
    id: source.id || payload.id || source.customerId || payload.customerId || source.userId || payload.userId || source._id || payload._id || '',
    firstName: source.firstName || source.first_name || payload.firstName || payload.first_name || '',
    lastName: source.lastName || source.last_name || payload.lastName || payload.last_name || '',
    email: source.email || payload.email || '',
    phone:
      source.phone ||
      source.mobile ||
      source.mobileNumber ||
      source.phoneNumber ||
      payload.phone ||
      payload.mobile ||
      payload.mobileNumber ||
      payload.phoneNumber ||
      '',
    active: Boolean(source.active ?? payload.active ?? true),
    walletBalance: Number(source.walletBalance ?? source.wallet_balance ?? payload.walletBalance ?? payload.wallet_balance ?? 0),
    emailVerified: Boolean(source.emailVerified ?? source.email_verified ?? payload.emailVerified ?? payload.email_verified ?? false),
    createdAt: source.createdAt || source.created_at || payload.createdAt || payload.created_at || null,
  };
};

const normalizePageResponse = (responseData, normalizeItem) => {
  const payload = extractPayload(responseData);
  const content = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.content)
      ? payload.content
      : Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload?.drivers)
          ? payload.drivers
          : Array.isArray(payload?.customers)
            ? payload.customers
            : [];

  return {
    content: content.map(normalizeItem),
    totalElements: Number(payload?.totalElements ?? payload?.total_elements ?? content.length),
    totalPages: Number(payload?.totalPages ?? payload?.total_pages ?? 1),
    currentPage: Number(payload?.currentPage ?? payload?.current_page ?? payload?.number ?? 0),
  };
};

const getValidationCode = (payload) => Number(
  payload?.code ||
  payload?.statusCode ||
  payload?.status_code ||
  payload?.httpStatus ||
  payload?.http_status ||
  payload?.responseCode ||
  payload?.response_code ||
  0
);

const normalizeValidationResponse = (payload, fallbackMessage = 'Validation failed') => {
  const validationPayload = extractPayload(payload) || payload || {};
  const code = getValidationCode(validationPayload);
  const success = code === 200 && validationPayload.success === true && validationPayload.valid === true;

  return {
    ...validationPayload,
    code,
    success,
    message:
      validationPayload.message ||
      validationPayload.statusMessage ||
      validationPayload.status_message ||
      validationPayload.error ||
      fallbackMessage,
  };
};

export const fetchDriversForApproval = createAsyncThunk(
  'admin/fetchDriversForApproval',
  async ({ page = 0, size = 20 } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/admin/drivers', { params: { page, size } });
      return normalizePageResponse(response.data, normalizeAdminDriver);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to load drivers for approval'));
    }
  }
);

export const fetchAdminCustomers = createAsyncThunk(
  'admin/fetchAdminCustomers',
  async ({ page = 0, size = 20 } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/admin/customers', { params: { page, size } });
      return normalizePageResponse(response.data, normalizeAdminCustomer);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to load customers'));
    }
  }
);

export const setDriverActiveStatus = createAsyncThunk(
  'admin/setDriverActiveStatus',
  async ({ driverId, active }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch('/admin/drivers/' + encodeURIComponent(driverId) + '/active', null, { params: { active } });
      if (response.data?.success === false) {
        throw new Error(response.data.message || 'Failed to update driver active status');
      }
      const driver = extractPayload(response.data);
      return { driverId, driver: driver?.id ? driver : null };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update driver active status'));
    }
  }
);

export const setCustomerActiveStatus = createAsyncThunk(
  'admin/setCustomerActiveStatus',
  async ({ customerId, active }, { rejectWithValue }) => {
    try {
      await axiosInstance.patch(`/admin/customers/${customerId}/active`, null, { params: { active } });
      return { customerId, active };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update customer active status'));
    }
  }
);

export const validateDriverLicense = createAsyncThunk(
  'admin/validateDriverLicense',
  async (driver, { rejectWithValue }) => {
    const request = {
      driverId: driver.id,
      licenseNumber: driver.licenseNumber,
      phone: driver.phone,
      email: driver.email,
      firstName: driver.firstName,
      lastName: driver.lastName,
    };

    try {
      const response = await axiosInstance.post(`/admin/drivers/${driver.id}/validate-license`, request);
      const validation = normalizeValidationResponse(response.data, 'DL validation failed');

      if (!validation.success) {
        return rejectWithValue({
          driverId: driver.id,
          field: 'license',
          message: validation.message,
          response: validation,
          request,
        });
      }

      return { driverId: driver.id, field: 'license', response: validation, request };
    } catch (error) {
      return rejectWithValue({
        driverId: driver.id,
        field: 'license',
        message: getErrorMessage(error, 'DL validation failed'),
        request,
      });
    }
  }
);

export const validateDriverVehicle = createAsyncThunk(
  'admin/validateDriverVehicle',
  async (driver, { rejectWithValue }) => {
    const request = {
      driverId: driver.id,
      vehicleNumber: driver.vehicleNumber,
      chassisNumber: driver.chassisNumber,
      phone: driver.phone,
      email: driver.email,
      firstName: driver.firstName,
      lastName: driver.lastName,
    };

    try {
      const response = await axiosInstance.post(`/admin/drivers/${driver.id}/validate-vehicle`, request);
      const validation = normalizeValidationResponse(response.data, 'Vehicle validation failed');

      if (!validation.success) {
        return rejectWithValue({
          driverId: driver.id,
          field: 'vehicle',
          message: validation.message,
          response: validation,
          request,
        });
      }

      return { driverId: driver.id, field: 'vehicle', response: validation, request };
    } catch (error) {
      return rejectWithValue({
        driverId: driver.id,
        field: 'vehicle',
        message: getErrorMessage(error, 'Vehicle validation failed'),
        request,
      });
    }
  }
);

export const approveDriverAccount = createAsyncThunk(
  'admin/approveDriverAccount',
  async (approvalRequest, { rejectWithValue, dispatch, getState }) => {
    const driver = approvalRequest?.driver || approvalRequest;
    const reason = approvalRequest?.reason || '';
    const driverId = typeof driver === 'object' ? driver.id : driver;

    try {
      const response = await axiosInstance.post(`/admin/drivers/${encodeURIComponent(driverId)}/approve`, { reason });
      const payload = extractPayload(response.data);
      if (payload?.id || payload?.driver?.id) {
        dispatch(adminSlice.actions.applyDriverReview(payload.driver || payload));
      }
      await dispatch(fetchDriversForApproval({ page: getState().admin.driversPagination.currentPage, size: 20 }));
      return null;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to approve driver'));
    }
  }
);

export const rejectDriverAccount = createAsyncThunk(
  'admin/rejectDriverAccount',
  async ({ driver, reason }, { rejectWithValue, dispatch, getState }) => {
    const driverId = driver?.id;

    try {
      if (!reason?.trim()) throw new Error('Enter a reason for rejecting the documents.');
      const response = await axiosInstance.post(`/admin/drivers/${encodeURIComponent(driverId)}/reject`, { reason: reason.trim() });
      const payload = extractPayload(response.data);
      if (payload?.id || payload?.driver?.id) {
        dispatch(adminSlice.actions.applyDriverReview(payload.driver || payload));
      }
      await dispatch(fetchDriversForApproval({ page: getState().admin.driversPagination.currentPage, size: 20 }));
      return null;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to reject driver'));
    }
  }
);

const initialState = {
  drivers: [],
  customers: [],
  driversPagination: {
    totalElements: 0,
    totalPages: 1,
    currentPage: 0,
  },
  customersPagination: {
    totalElements: 0,
    totalPages: 1,
    currentPage: 0,
  },
  loading: false,
  customersLoading: false,
  error: null,
  customersError: null,
  approvalLoadingId: null,
  activeStatusLoadingId: null,
  validationLoading: {},
  validationResults: {},
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    applyDriverReview: (state, action) => {
      const index = state.drivers.findIndex((driver) => driver.id === action.payload.id);
      if (index >= 0) {
        state.drivers[index] = normalizeAdminDriver({ ...state.drivers[index], ...action.payload });
      }
    },
    clearAdminError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDriversForApproval.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDriversForApproval.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.drivers = action.payload.content;
        state.driversPagination = {
          totalElements: action.payload.totalElements,
          totalPages: action.payload.totalPages,
          currentPage: action.payload.currentPage,
        };
        state.validationLoading = {};
        state.validationResults = {};
      })
      .addCase(fetchDriversForApproval.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(approveDriverAccount.pending, (state, action) => {
        state.error = null;
        const driver = action.meta.arg?.driver || action.meta.arg;
        state.approvalLoadingId = typeof driver === 'object' ? driver.id : driver;
      })
      .addCase(approveDriverAccount.fulfilled, (state) => {
        state.approvalLoadingId = null;
      })
      .addCase(approveDriverAccount.rejected, (state, action) => {
        state.approvalLoadingId = null;
        state.error = action.payload;
      })
      .addCase(fetchAdminCustomers.pending, (state) => {
        state.customersLoading = true;
        state.customersError = null;
      })
      .addCase(fetchAdminCustomers.fulfilled, (state, action) => {
        state.customersLoading = false;
        state.customersError = null;
        state.customers = action.payload.content;
        state.customersPagination = {
          totalElements: action.payload.totalElements,
          totalPages: action.payload.totalPages,
          currentPage: action.payload.currentPage,
        };
      })
      .addCase(fetchAdminCustomers.rejected, (state, action) => {
        state.customersLoading = false;
        state.customersError = action.payload;
      })
      .addCase(rejectDriverAccount.pending, (state, action) => {
        state.error = null;
        state.approvalLoadingId = action.meta.arg.driver?.id;
      })
      .addCase(rejectDriverAccount.fulfilled, (state) => {
        state.approvalLoadingId = null;
      })
      .addCase(rejectDriverAccount.rejected, (state, action) => {
        state.approvalLoadingId = null;
        state.error = action.payload;
      })
      .addCase(setDriverActiveStatus.pending, (state, action) => {
        state.error = null;
        state.activeStatusLoadingId = `driver-${action.meta.arg.driverId}`;
      })
      .addCase(setDriverActiveStatus.fulfilled, (state, action) => {
        state.activeStatusLoadingId = null;
        const index = state.drivers.findIndex((driver) => driver.id === action.payload.driverId);
        if (index >= 0) {
          if (action.payload.driver) {
            state.drivers[index] = normalizeAdminDriver({ ...state.drivers[index], ...action.payload.driver });
          }
        }
      })
      .addCase(setDriverActiveStatus.rejected, (state, action) => {
        state.activeStatusLoadingId = null;
        state.error = action.payload;
      })
      .addCase(setCustomerActiveStatus.pending, (state, action) => {
        state.customersError = null;
        state.activeStatusLoadingId = `customer-${action.meta.arg.customerId}`;
      })
      .addCase(setCustomerActiveStatus.fulfilled, (state, action) => {
        state.activeStatusLoadingId = null;
        const index = state.customers.findIndex((customer) => customer.id === action.payload.customerId);
        if (index >= 0) {
          state.customers[index].active = action.payload.active;
        }
      })
      .addCase(setCustomerActiveStatus.rejected, (state, action) => {
        state.activeStatusLoadingId = null;
        state.customersError = action.payload;
      })
      .addCase(validateDriverLicense.pending, (state, action) => {
        const driverId = action.meta.arg.id;
        state.error = null;
        state.validationLoading[`${driverId}-license`] = true;
        state.validationResults[driverId] = {
          ...state.validationResults[driverId],
          license: { status: 'pending', message: 'Validating DL...' },
        };
      })
      .addCase(validateDriverLicense.fulfilled, (state, action) => {
        const { driverId, field, response, request } = action.payload;
        state.validationLoading[`${driverId}-${field}`] = false;
        state.validationResults[driverId] = {
          ...state.validationResults[driverId],
          [field]: { status: 'valid', message: response.message, response, request },
        };
      })
      .addCase(validateDriverLicense.rejected, (state, action) => {
        const driverId = action.payload?.driverId || action.meta.arg.id;
        const field = action.payload?.field || 'license';
        state.validationLoading[`${driverId}-${field}`] = false;
        state.validationResults[driverId] = {
          ...state.validationResults[driverId],
          [field]: {
            status: 'invalid',
            message: action.payload?.message || 'DL validation failed',
            response: action.payload?.response || null,
            request: action.payload?.request || null,
          },
        };
      })
      .addCase(validateDriverVehicle.pending, (state, action) => {
        const driverId = action.meta.arg.id;
        state.error = null;
        state.validationLoading[`${driverId}-vehicle`] = true;
        state.validationResults[driverId] = {
          ...state.validationResults[driverId],
          vehicle: { status: 'pending', message: 'Validating vehicle...' },
        };
      })
      .addCase(validateDriverVehicle.fulfilled, (state, action) => {
        const { driverId, field, response, request } = action.payload;
        state.validationLoading[`${driverId}-${field}`] = false;
        state.validationResults[driverId] = {
          ...state.validationResults[driverId],
          [field]: { status: 'valid', message: response.message, response, request },
        };
      })
      .addCase(validateDriverVehicle.rejected, (state, action) => {
        const driverId = action.payload?.driverId || action.meta.arg.id;
        const field = action.payload?.field || 'vehicle';
        state.validationLoading[`${driverId}-${field}`] = false;
        state.validationResults[driverId] = {
          ...state.validationResults[driverId],
          [field]: {
            status: 'invalid',
            message: action.payload?.message || 'Vehicle validation failed',
            response: action.payload?.response || null,
            request: action.payload?.request || null,
          },
        };
      });
  },
});

export const { clearAdminError } = adminSlice.actions;
export default adminSlice.reducer;
